import { Option } from 'baseui/select';
import stylesColors from 'common/styles/styles.colors';
import { generateId } from 'common/utils/string';
import Separator from 'components/common/separator';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import { ColumnDataType, IColumn } from 'components/elements/table/type';
import Text from 'components/elements/text';
import { HeaderCheckbox } from 'legacy-modules/sales/reschedule/header-checkbox';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import {
  ActivityRescheduleFormType,
  ActivitySaleAddOnsType,
} from '../../form-type';

function reducer(
  state,
  action,
  { update, remove, clearErrors, name, setValue },
) {
  switch (action.type) {
    case 'addRow':
      return {
        ...state,
        data: [...state.data, { customId: generateId() }],
      };
    case 'removeRow':
      // eslint-disable-next-line no-case-declarations
      const updateRow = [
        ...state.data.slice(0, action.rowIndex),
        ...state.data.slice(action.rowIndex + 1, state.data.length),
      ];
      remove(action.rowIndex);
      clearErrors(name);
      return {
        ...state,
        data: updateRow,
      };
    case 'update_cell':
      return {
        ...state,
        data: state.data.map((row, index) => {
          if (index === action.rowIndex) {
            const tempValue = {
              ...state.data[action.rowIndex],
              [action.columnId]: action.value,
            };
            // update(index, tempValue);
            clearErrors(name);
            setValue(`${name}.${index}`, tempValue, { shouldValidate: true });
            return tempValue;
          }
          return row;
        }),
      };
    case 'update_all':
      return {
        ...state,
        data: action.newData,
      };
    case 'isSelected':
      return {
        ...state,
        data: state.data.map((row, index) => {
          const tempValue = {
            ...state.data[index],
            isSelected: action.value,
          };
          clearErrors(name);
          // update(index, tempValue);
          setValue(`${name}.${index}`, tempValue, { shouldValidate: true });

          return tempValue;
        }),
      };
    default:
      return state;
  }
}

const _columns: (props: {
  t: any;
  editable?: boolean;
  guestOptions: Option[];
  dispatch?: any;
  name: any;
}) => IColumn<ActivitySaleAddOnsType>[] = ({
  t,
  editable,
  guestOptions,
  dispatch,
  name,
}) => [
  {
    header: () => <HeaderCheckbox name={name} dispatch={dispatch} />,
    accessorKey: 'isSelected',
    minSize: 50,
    size: 50,
    checkboxNoLabel: true,
    dataType: ColumnDataType.checkbox,
  },
  {
    header: t('common:guest'),
    accessorKey: 'guestId',
    minSize: 250,
    dataType: ColumnDataType.select,
    required: true,
    options: guestOptions,
  },
  {
    header: t('common:description'),
    accessorKey: 'description',
    minSize: 100,
    dataType: ColumnDataType.text,
  },
  {
    header: t('common:price'),
    accessorKey: 'price',
    minSize: 100,
    dataType: ColumnDataType.number,
    minNumber: 0,
  },
];

export default function ActivityRescheduleAddOnsTable({
  parentIndex,
}: {
  parentIndex: number;
}) {
  const { t } = useTranslation();
  const { editable } = useFormState();

  const {
    control,
    formState: { isSubmitting },
    clearErrors,
    setValue,
  } = useFormContext<ActivityRescheduleFormType>();

  const name: any = `activitySales.${parentIndex}.activitySaleAddOns`;

  const { fields, update, remove } = useFieldArray({
    control,
    name,
    keyName: 'customId',
  });

  const [activitySales] = useWatch({
    control,
    name: ['activitySales'],
  });

  const guestOptions = activitySales[parentIndex].activitySaleGuests
    .filter(
      (value, index, self) =>
        self.findIndex((m) => m.guestId === value.guestId) === index,
    )
    .map((hsg) => ({ id: hsg.guestId, label: hsg.guestName }));

  const columns = (dispatch?: any) =>
    _columns({ t, editable, guestOptions, name, dispatch });

  const [isInitiate, setIsInitiate] = React.useState<{
    value: boolean;
    data: any;
  }>({ value: false, data: undefined });

  const [state, dispatch] = React.useReducer(
    (state, action) =>
      reducer(state, action, { update, remove, clearErrors, name, setValue }),
    {
      columns: columns(),
      data: [],
    },
  );

  React.useEffect(() => {
    if (!isInitiate.value) {
      dispatch({
        type: 'update_all',
        newData: fields.length ? fields : [],
      });
      setIsInitiate({
        value: true,
        data: fields.length ? fields : [],
      });
    }
  }, [fields, isInitiate]);

  React.useEffect(() => {
    if (!editable && isInitiate.value && !isSubmitting) {
      dispatch({ type: 'update_all', newData: isInitiate.data });
    }
  }, [editable, isInitiate.value, isSubmitting, t, isInitiate.data]);

  if (fields.length === 0) {
    return null;
  }

  return (
    <>
      <Separator gap={16} direction="vertical">
        <Text
          variant="LabelMedium"
          $style={{ color: stylesColors.primaryDefault }}
        >
          {t('document:addons')}
        </Text>
      </Separator>
      <div style={{ width: '100%' }}>
        <TableInput
          columns={columns(dispatch) as any}
          data={state.data}
          dispatch={dispatch}
          disabled={!editable}
          name={name}
          noAddRemoveRow
          uniqueRowKey="customId"
          onCustomDisabledCell={({ accessorKey, rowOriginal }) => {
            if (accessorKey !== 'isSelected' && !rowOriginal.isSelected) {
              return true;
            }
            return false;
          }}
        />
      </div>
    </>
  );
}
